import { useCallback, useEffect, useRef, useState } from 'react';
import { AthenaServices } from '../../../../lib/athena/athena-services';

interface UseAutocompleteProps {
  isAllergyQuestion: boolean;
  isMedicationQuestion: boolean;
  isPharmacyQuestion: boolean;
  isPCPQuestion: boolean;
  isNestedAllergyQuestion?: boolean;
  questionKey?: string;
  surveyState: any;
  setSurveyStateRef: React.MutableRefObject<any>;
  onUpdateRef: React.MutableRefObject<any>;
}

export const useAutocomplete = ({
  isAllergyQuestion,
  isMedicationQuestion,
  isPharmacyQuestion,
  isPCPQuestion,
  isNestedAllergyQuestion = false,
  questionKey,
  surveyState,
  setSurveyStateRef,
  onUpdateRef,
}: UseAutocompleteProps) => {
  // 자동완성 관련 상태
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [autocompleteItems, setAutocompleteItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 기존 데이터 상태
  const [existMedication, setExistMedication] = useState<any[]>([]);
  const [existPharmacy, setExistPharmacy] = useState<any>(null);
  const [existPCP, setExistPCP] = useState<any[]>([]);
  const [existAllergy, setExistAllergy] = useState<any[]>([]);
  const [existAllergyFinal, setExistAllergyFinal] = useState<any[]>([]);

  // 체크 플래그
  const [checkedMedication, setCheckedMedication] = useState(false);
  const [checkedPharmacy, setCheckedPharmacy] = useState(false);
  const [checkedPCP, setCheckedPCP] = useState(false);
  const [checkedAllergy, setCheckedAllergy] = useState(false);

  // 자동완성에서 선택된 값 저장용
  const [selectedAutoValue, setSelectedAutoValue] = useState<string | null>(null);

  // 기존 데이터 불러오기 함수들
  const checkMedication = useCallback(async () => {
    if (isMedicationQuestion) {
      try {
        const result = await AthenaServices.findExistMedication(surveyState?.patientDetails?.patientid, surveyState?.patientDetails?.departmentid);

        if (result.data.medications) {
          setExistMedication(result.data.medications);
        } else {
          setExistMedication([]);
        }
      } catch (error) {
        setExistMedication([]);
      }
    }
  }, [isMedicationQuestion, surveyState?.patientDetails?.patientid, surveyState?.patientDetails?.departmentid]);

  const checkPharmacy = useCallback(async () => {
    if (isPharmacyQuestion) {
      try {
        const result = await AthenaServices.findExistPharmacy(surveyState?.patientDetails?.patientid, surveyState?.patientDetails?.departmentid);
        if (result?.data) {
          setExistPharmacy(result.data);
        } else {
          setExistPharmacy(null);
        }
      } catch (error) {
        setExistPharmacy(null);
      }
    }
  }, [isPharmacyQuestion, surveyState?.patientDetails?.patientid, surveyState?.patientDetails?.departmentid]);

  const checkPCP = useCallback(async () => {
    if (isPCPQuestion) {
      try {
        const result = await AthenaServices.findExistProvider(surveyState?.patientDetails?.patientid, surveyState?.patientDetails?.departmentid);
        if (result?.data?.members) {
          setExistPCP(result.data.members);
        } else {
          setExistPCP([]);
        }
      } catch (error) {
        setExistPCP([]);
      }
    }
  }, [isPCPQuestion, surveyState?.patientDetails?.patientid, surveyState?.patientDetails?.departmentid]);

  const checkAllergy = useCallback(async () => {
    if (isNestedAllergyQuestion) {
      try {
        const result = await AthenaServices.findExistAllergy(surveyState?.patientDetails?.patientid, surveyState?.patientDetails?.departmentid);
        if (result?.data?.allergies) {
          setExistAllergyFinal(result.data.allergies);
          setSurveyStateRef.current((prev: any) => ({
            ...prev,
            allergyFinal: result.data.allergies,
          }));
        } else {
          setExistAllergyFinal([]);
        }
        setCheckedAllergy(true);
      } catch (error) {
        setExistAllergyFinal([]);
        setCheckedAllergy(true);
      }
    }
  }, [isNestedAllergyQuestion, surveyState?.patientDetails?.patientid, surveyState?.patientDetails?.departmentid, setSurveyStateRef]);

  // existMedication이 변경될 때 autoFillValue 실행
  useEffect(() => {
    if (existMedication && existMedication.length > 0 && !selectedAutoValue) {
      // autoFillValue 함수를 실제로 호출
      autoFillValue('', () => { });
    }
  }, [existMedication, selectedAutoValue]);

  // 최초 자동완성 데이터 불러오기
  useEffect(() => {
    if (isMedicationQuestion && surveyState?.checkFlag && !checkedMedication) {
      checkMedication();
      setCheckedMedication(true);
    }
    if (isPharmacyQuestion && surveyState?.checkFlag && !checkedPharmacy) {
      checkPharmacy();
      setCheckedPharmacy(true);
    }
    if (isPCPQuestion && surveyState?.checkFlag && !checkedPCP) {
      checkPCP();
      setCheckedPCP(true);
    }
    if (isNestedAllergyQuestion && surveyState?.checkFlag && !checkedAllergy) {
      checkAllergy();
    }
  }, [
    isMedicationQuestion, isPharmacyQuestion, isPCPQuestion, isAllergyQuestion, isNestedAllergyQuestion,
    checkMedication, checkPharmacy, checkPCP, checkAllergy,
    surveyState?.checkFlag,
    checkedMedication, checkedPharmacy, checkedPCP, checkedAllergy
  ]);

  // 자동완성 데이터가 들어오면 자동으로 값 채우기 (최초 1회만)
  const autoFillValue = useCallback((_valueTemp: string, setValueTemp: (value: string) => void) => {
    // Nested Allergy Question - 모든 알러지를 Nested Options 형태로 변환
    if (
      isNestedAllergyQuestion &&
      existAllergy &&
      existAllergy.length > 0 &&
      !selectedAutoValue &&
      (!surveyState?.allergyFinal || surveyState?.allergyFinal.length === 0)
    ) {
      const allergyFinal = existAllergy.map(allergy => ({
        allergy: allergy.allergenname || '',
        reactions: allergy.reactions?.map((reaction: any) => reaction.reactionname || '') || []
      }));

      if (allergyFinal.length > 0) {
        setSurveyStateRef.current((prev: any) => ({
          ...prev,
          allergyFinal,
        }));

        // QsInputMultiTextNested에서 처리할 수 있도록 문자열로 변환
        const combinedValue = allergyFinal.map(allergy => allergy.allergy).join('|');
        setSelectedAutoValue(combinedValue);
        onUpdateRef.current(combinedValue);
        setShowAutocomplete(false);
      }
    }
    // Medication Question - 모든 medication을 태그 형태로 변환
    if (
      isMedicationQuestion &&
      existMedication &&
      existMedication.length > 0 &&
      !selectedAutoValue &&
      (!_valueTemp || _valueTemp.trim() === '') // 사용자가 입력한 값이 없을 때만 실행
    ) {
      // 중첩된 배열을 평면화하고 medication 이름만 추출
      const medicationNames = existMedication
        .flat() // 중첩된 배열을 평면화
        .map((med: any) => med.medication || '')
        .filter((name: string) => name && name.trim() !== '')
        .filter((name: string, index: number, arr: string[]) => arr.indexOf(name) === index); // 중복 제거

      const combinedValue = medicationNames.join('|');

      if (combinedValue) {
        // q-medication-pta-describe는 medicationCurrent에 저장
        // q-dhx-description-athena는 currentMedication에 저장
        const isPtaDescribe = questionKey === 'q-medication-pta-describe';
        const isDhxDescription = questionKey === 'q-dhx-description-athena' || questionKey === 'q-dhx-description';

        if (isPtaDescribe) {
          setSurveyStateRef.current((prev: any) => ({
            ...prev,
            medicationCurrent: combinedValue,
          }));
        } else if (isDhxDescription) {
          setSurveyStateRef.current((prev: any) => ({
            ...prev,
            currentMedication: combinedValue,
          }));
        } else {
          // 기본값: medicationCurrent에 저장 (기존 동작 유지)
          setSurveyStateRef.current((prev: any) => ({
            ...prev,
            medicationCurrent: combinedValue,
          }));
        }
        setSelectedAutoValue(combinedValue);
        setShowAutocomplete(false);
      }
    }
    // Pharmacy
    if (isPharmacyQuestion && existPharmacy && !selectedAutoValue) {
      const rawName = existPharmacy.clinicalprovidername || existPharmacy.pharmacyName || '';
      const name = rawName.replace(/\s*#\d+/g, '');
      if (name) {
        setSurveyStateRef.current((prev: any) => ({
          ...prev,
          pharmacyId: existPharmacy.clinicalproviderid,
        }));
        setSelectedAutoValue(name);
        onUpdateRef.current(name);
        setShowAutocomplete(false);
      }
    }
    // PCP
    if (isPCPQuestion && existPCP && existPCP.length > 0 && !selectedAutoValue) {
      // recipientclass.code가 3인 PCP만 선택
      const firstPCP = existPCP.find((pcp: any) => pcp.recipientclass?.code === 3) || existPCP[0];
      const name = firstPCP?.name || firstPCP?.providerName || '';
      if (name) {
        setSurveyStateRef.current((prev: any) => ({
          ...prev,
          providerId: firstPCP.clinicalproviderid,
        }));
        setSelectedAutoValue(name);
        onUpdateRef.current(name);
        setShowAutocomplete(false);
      }
    }
  }, [
    isNestedAllergyQuestion,
    existAllergy,
    selectedAutoValue,
    surveyState?.allergyFinal,
    isAllergyQuestion,
    surveyState?.allergy,
    isMedicationQuestion,
    existMedication,
    surveyState?.medicationCurrent,
    isPharmacyQuestion,
    existPharmacy,
    surveyState?.pharmacy,
    isPCPQuestion,
    existPCP,
    surveyState?.pcp,
    setSurveyStateRef,
    onUpdateRef
  ]);

  // 아이템 선택 핸들러
  const handleSelectItem = useCallback((item: any, setValueTemp: (value: string) => void) => {
    let selectedValue = '';
    if (isAllergyQuestion) {
      selectedValue = item.allergy || '';
    } else if (isNestedAllergyQuestion) {
      selectedValue = item.allergy || '';
    } else if (isMedicationQuestion) {
      selectedValue = item.medication || '';
      setSurveyStateRef.current((prev: any) => ({
        ...prev,
        medication: selectedValue,
      }));
    } else if (isPharmacyQuestion) {
      const rawName = item.name || item.pharmacyName || '';
      selectedValue = rawName.replace(/\s*#\d+/g, '');

      setSurveyStateRef.current((prev: any) => ({
        ...prev,
        pharmacyId: item.clinicalproviderid,
        pharmacyName: selectedValue,
      }));
    } else if (isPCPQuestion) {
      selectedValue = item.name || item.providerName || '';

      // PCP 정보를 CCDA XML 형태로 생성
      const pcpCCDAXml = `
<performer typeCode="PRF">
  <templateId root="2.16.840.1.113883.10.20.6.2.1" />
  <functionCode code="3" displayName="Primary Care Provider" />
  <assignedEntity>
    <id extension="${item.clinicalproviderid}" root="2.16.840.1.113883.4.6" />
    <code code="207Q00000X" codeSystem="2.16.840.1.113883.6.101" codeSystemName="NUCC" displayName="Family Practice" />
    <addr use="WP">
      <streetAddressLine>${item.address || ''}</streetAddressLine>
      <streetAddressLine nullFlavor="NI" />
      <city>${item.city || ''}</city>
      <state>${item.state || ''}</state>
      <postalCode>${item.zip || ''}</postalCode>
      <country>US</country>
    </addr>
    <telecom use="WP" value="tel:${item.phone || ''}" />
    <assignedPerson>
      <name>
        <given>${item.firstname || ''}</given>
        <family>${item.lastname || ''}</family>
      </name>
    </assignedPerson>
    <representedOrganization>
      <id root="${item.clinicalproviderid}" />
      <name nullFlavor="NI" />
      <telecom use="WP" value="tel:${item.phone || ''}" />
      <addr use="WP">
        <streetAddressLine>${item.address || ''}</streetAddressLine>
        <streetAddressLine nullFlavor="NI" />
        <city>${item.city || ''}</city>
        <state>${item.state || ''}</state>
        <postalCode>${item.zip || ''}</postalCode>
        <country>US</country>
      </addr>
    </representedOrganization>
  </assignedEntity>
</performer>`;

      setSurveyStateRef.current((prev: any) => ({
        ...prev,
        providerId: item.clinicalproviderid,
        providerName: selectedValue,
        providerCCDA: pcpCCDAXml, // CCDA XML도 함께 저장
      }));
    }

    setValueTemp(selectedValue);
    setSelectedAutoValue(selectedValue);
    onUpdateRef.current(selectedValue);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
  }, [isAllergyQuestion, isNestedAllergyQuestion, isMedicationQuestion, isPharmacyQuestion, isPCPQuestion, setSurveyStateRef, onUpdateRef]);

  return {
    // 상태
    showAutocomplete,
    setShowAutocomplete,
    selectedIndex,
    setSelectedIndex,
    autocompleteItems,
    setAutocompleteItems,
    isLoading,
    setIsLoading,
    selectedAutoValue,
    setSelectedAutoValue,
    existMedication,
    existPharmacy,
    existPCP,
    existAllergy,

    // 함수들
    autoFillValue,
    handleSelectItem,
    checkMedication,
    checkPharmacy,
    checkPCP,
    checkAllergy,
  };
};
